/**
 * "First Steps" — original onboarding level (difficulty 1/10).
 * Authored in block coordinates (1 block = 30 units) via the tiny builder
 * below; produces standard SSL LevelData like any editor-made level.
 * Layout teaches: single jumps → jump timing → double spikes → steps/platforms
 * → triple spike finale. ~38 s at 1× speed, synced to "Neon Circuit" (128 BPM).
 */
import type { LevelData, LevelObject } from '../../format/types';
import { UNITS_PER_BLOCK as B } from '../../engine/physics/constants';

let nextId = 1;
const objects: LevelObject[] = [];

/** Center of block-cell (bx, by): by = 0 sits on the ground. */
function cell(bx: number, by: number): { x: number; y: number } {
  return { x: bx * B + B / 2, y: by * B + B / 2 };
}

function spike(bx: number, by = 0): void {
  objects.push({ id: nextId++, type: 'spike', ...cell(bx, by) });
}

function block(bx: number, by = 0): void {
  objects.push({ id: nextId++, type: 'block', ...cell(bx, by) });
}

/** Solid column of `h` blocks tall at bx. */
function column(bx: number, h: number): void {
  for (let i = 0; i < h; i++) block(bx, i);
}

/** Horizontal platform at height `by` spanning [bx0, bx1]. */
function platform(bx0: number, bx1: number, by: number): void {
  for (let bx = bx0; bx <= bx1; bx++) block(bx, by);
}

function pylon(bx: number): void {
  objects.push({ id: nextId++, type: 'decoration', ...cell(bx, 0), props: { kind: 'pylon' } });
}

// --- Act 1: single jumps (bars 1–8) -----------------------------------------
pylon(10);
spike(28);
spike(38);
spike(48);
pylon(54);
spike(58);
spike(64); // slightly tighter rhythm
spike(70);

// --- Act 2: doubles & first steps (bars 9–16) --------------------------------
spike(78);
spike(79);
pylon(84);
platform(90, 94, 0); // low ledge to hop onto
spike(98); // spike shortly after the ledge drop
spike(104);
spike(105);
column(112, 1);
column(114, 2);
platform(115, 119, 1); // stair up to plateau
spike(117, 2); // spike on the plateau
pylon(124);

// --- Act 3: rhythm chains (bars 17–24) ---------------------------------------
spike(130);
spike(135);
spike(136);
spike(142);
spike(147);
spike(148);
pylon(153);
// Elevated escape: spikes below, platform above (step block eases the climb)
column(156, 1);
platform(158, 166, 1);
for (let bx = 159; bx <= 165; bx += 2) spike(bx);
spike(171);
spike(172);

// --- Act 4: finale (bars 25–32) ----------------------------------------------
spike(180);
spike(181); // double
spike(190);
column(196, 1);
platform(196, 199, 0);
spike(198, 1); // spike on the ledge
spike(206);
spike(207);
spike(214);
spike(215); // final double
pylon(224);

// Secret shards: optional pickups along deliberate jump arcs.
objects.push({ id: nextId++, type: 'collectible', ...cell(33, 2), props: { kind: 'shard' } });
objects.push({ id: nextId++, type: 'collectible', ...cell(100, 2), props: { kind: 'shard' } });
objects.push({ id: nextId++, type: 'collectible', ...cell(152, 2), props: { kind: 'shard' } });

// Endgate
objects.push({ id: nextId++, type: 'endgate', ...cell(232, 2) });

export const FIRST_STEPS: LevelData = {
  formatVersion: 1,
  meta: {
    name: 'First Steps',
    author: 'Shape Shifters Team',
    description: 'Learn to run, jump, and feel the beat.',
    revision: 1,
    createdAt: '2026-07-23T00:00:00.000Z',
    updatedAt: '2026-07-23T00:00:00.000Z',
    difficulty: 1,
    tags: ['tutorial', 'official'],
  },
  song: { id: 'neon-circuit', bpm: 128, offsetMs: 0 },
  settings: {
    mode: 'normal',
    speed: 'one',
    accentColor: '#22d3ee',
    hazardColor: '#f43f5e',
    backgroundColor: '#0b0620',
  },
  objects,
};
